import { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { v4 as uuidv4 } from 'uuid';

const getListID = (droppableID) => {
  const splitID = droppableID.split('-');
  if (splitID.length !== 2) {
    throw new Error(`Invalid droppableID - expected 2 parts, got ${splitID.length}`);
  }
  return splitID[1];
};

const removeIndex = (stepList, idx) => {
  // splice() returns the list of removed elements - we're only ever removing one element, and we want that element unwrapped
  const spliced = stepList.splice(idx, 1);
  return spliced[0];
};

const insertAtIndex = (stepList, idx, stepsToInsert) => {
  stepList.splice(idx, 0, stepsToInsert);
};

const rebuildGroupResource = (groupResource, newActiveSteps, newInactiveSteps) => {
  // Rebuild the flat list of all ordered step resources
  const combinedResources = [];
  for (const steps of [newActiveSteps, newInactiveSteps]) {
    for (let i = 0; i < steps.length; i++) {
      combinedResources.push(...steps[i]);
    }
  }

  // Remove all step items from group resource and then add steps back in from combinedResources
  const groupResourceCopy = groupResource.copy();
  groupResourceCopy.attr('steps').resources().removeAllItems();
  for (let i = 0; i < combinedResources.length; i++) {
    const stepRes = combinedResources[i];
    groupResourceCopy.attr('steps').resources().pushItem(stepRes.id(), stepRes);
  }

  return groupResourceCopy;
};

const moveStepSet = (
  logStandardizedEvent,
  groupResource,
  activeSteps, inactiveSteps,
  source, destination,
) => {
  logStandardizedEvent(categories.whatIf, eventTypes.dragAndDrop, 'move step set within group');

  const sourceID = getListID(source.droppableId);
  const destinationID = getListID(destination.droppableId);

  const newActiveSteps = Array.from(activeSteps);
  const newInactiveSteps = Array.from(inactiveSteps);

  // Get the moving steps and remove them from their starting place
  let targetStepResources;
  if (sourceID === 'Active') {
    targetStepResources = removeIndex(newActiveSteps, source.index);
  } else {
    targetStepResources = removeIndex(newInactiveSteps, source.index);
  }

  // Update isActive if it was toggled by moving lists
  if (sourceID !== destinationID) {
    logStandardizedEvent(categories.whatIf, eventTypes.dragAndDrop, 'toggle step isActive');
    for (let i = 0; i < targetStepResources.length; i++) {
      const newValue = !targetStepResources[i].isTruthy('isActive');
      targetStepResources[i].attr('isActive', newValue.toString());
    }
  }

  // Insert the moving steps into their new ending place
  if (destinationID === 'Active') {
    insertAtIndex(newActiveSteps, destination.index, targetStepResources);
  } else {
    insertAtIndex(newInactiveSteps, destination.index, targetStepResources);
  }

  return rebuildGroupResource(groupResource, newActiveSteps, newInactiveSteps);
};

// Given a groupResource, break up a group within
const breakStepSetGroup = (groupResource, stepSet) => {
  const groupCopy = groupResource.copy();

  for (let i = 0; i < groupCopy.attr('steps').resources().length(); i++) {
    const stepRes = groupCopy.attr('steps').resources().index(i);

    if (stepRes.attr('stepSet') === stepSet) {
      stepRes.attr('stepSet', uuidv4());
    }
  }

  return groupCopy;
};

const isCompatibleDropTarget = (groupResource, draggingStepSetID, destinationStepSetID) => {
  // If comparing against yourself then just return null since we don't care to highlight in this case
  if (draggingStepSetID === destinationStepSetID) {
    return null;
  }

  const draggingStepResources = groupResource.attr('steps').resources().list().filter((step) => step.attr('stepSet') === draggingStepSetID);
  const draggingGroupName = draggingStepResources[0].attr('groupName');
  const draggingFundingMode = draggingStepResources[0].attr('fundingMode');

  const destinationStepResources = groupResource.attr('steps').resources().list().filter((step) => step.attr('stepSet') === destinationStepSetID);
  const destinationGroupName = destinationStepResources[0].attr('groupName');
  const destinationFundingMode = destinationStepResources[0].attr('fundingMode');

  return draggingGroupName === destinationGroupName && draggingFundingMode === destinationFundingMode;
};

const getStepSetIdFromDraggableID = (id) => id.split('.').slice(-1)[0];

const findMatchingStepSetGroupIndex = (stepLists, targetStepSetID) => {
  for (let i = 0; i < stepLists.length; i++) {
    // If the first step in the list matches the targetStepSetID, we've found the matching step set group
    if (stepLists[i][0].attr('stepSet') === targetStepSetID) {
      return i;
    }
  }

  throw new Error(`failed to find step set group for id ${targetStepSetID}`);
};

const combineInList = (stepLists, toCombineIntoStepSetID, stepsToCombine) => {
  const resourcesToCombineIntoIndex = findMatchingStepSetGroupIndex(stepLists, toCombineIntoStepSetID);
  const resourcesToCombineInto = removeIndex(stepLists, resourcesToCombineIntoIndex);
  const newCombinedResources = [...resourcesToCombineInto, ...stepsToCombine];
  insertAtIndex(stepLists, resourcesToCombineIntoIndex, newCombinedResources);
};

const combineStepSets = (
  logStandardizedEvent,
  groupResource,
  activeSteps, inactiveSteps,
  source, combine,
) => {
  logStandardizedEvent(categories.whatIf, eventTypes.dragAndDrop, 'combine allocation step sets');

  const sourceID = getListID(source.droppableId);
  const destinationID = getListID(combine.droppableId);

  const newActiveSteps = Array.from(activeSteps);
  const newInactiveSteps = Array.from(inactiveSteps);

  // Get the steps we're going to combine and remove them from their starting place
  let targetStepResources;
  if (sourceID === 'Active') {
    targetStepResources = removeIndex(newActiveSteps, source.index);
  } else {
    targetStepResources = removeIndex(newInactiveSteps, source.index);
  }

  // Check compatibility and bail if needed
  const toCombineIntoStepSetID = getStepSetIdFromDraggableID(combine.draggableId);
  const toCombineStepSetID = targetStepResources[0].attr('stepSet');
  if (!isCompatibleDropTarget(groupResource, toCombineIntoStepSetID, toCombineStepSetID)) {
    return undefined;
  }

  let toggleActive = false;
  if (sourceID !== destinationID) {
    logStandardizedEvent(categories.whatIf, eventTypes.dragAndDrop, 'toggle step isActive');
    toggleActive = true;
  }

  // Update stepSetID and isActive if it was toggled by moving lists
  for (let i = 0; i < targetStepResources.length; i++) {
    targetStepResources[i].attr('stepSet', toCombineIntoStepSetID);

    if (toggleActive === true) {
      const newValue = !targetStepResources[i].isTruthy('isActive');
      targetStepResources[i].attr('isActive', newValue.toString());
    }
  }

  // Find the steps we're combining into, remove them, append the steps we're going to combine, and reinsert the now combined step list
  if (destinationID === 'Active') {
    combineInList(newActiveSteps, toCombineIntoStepSetID, targetStepResources);
  } else {
    combineInList(newInactiveSteps, toCombineIntoStepSetID, targetStepResources);
  }

  return rebuildGroupResource(groupResource, newActiveSteps, newInactiveSteps);
};

export default {
  moveStepSet,
  combineStepSets,
  breakStepSetGroup,
  isCompatibleDropTarget,
};
